import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  PaperclipIcon,
  ViewIcon,
  EditIcon,
  DeleteIcon,
  CloseIcon,
} from "../../../../Icons";
import {
  getClinicalFormAttachments,
  downloadClinicalFormAttachment,
  updateClinicalFormAttachment,
  deleteClinicalFormAttachment,
  getDailyLogAttachments,
  downloadDailyLogAttachment,
  updateDailyLogAttachment,
  deleteDailyLogAttachment,
} from "../../../../services/patientService";
import useConfirm from "../../../../hooks/useConfirm";
import { displayDateUS } from "../../../../utils/dateUtils";
import { clinicalFormScheduleDate } from "./sortClinicalForms";
import {
  MODAL_BACKDROP,
  MODAL_PANEL,
  MODAL_HEADER,
  MODAL_TITLE,
  MODAL_BODY,
  BTN_PRIMARY,
  BTN_SECONDARY,
} from "../../../../ui/pageChrome";

/**
 * Paperclip pin next to form name.
 * Opens a portaled manage dialog (table overflow must not clip it).
 */
const ClinicalFormAttachmentPin = ({
  patientId,
  form,
  canEdit,
  onChanged,
}) => {
  const [manageOpen, setManageOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [openingId, setOpeningId] = useState(null);
  const { confirm, confirmDialog } = useConfirm();

  const isDailyLog = form?.source === "daily-log";
  const targetId = isDailyLog ? form.dailyLogId : form.id;

  const count =
    typeof form.attachmentCount === "number"
      ? form.attachmentCount
      : (form.recentAttachments || []).length;

  const loadAll = async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const res = isDailyLog
        ? await getDailyLogAttachments(patientId, targetId)
        : await getClinicalFormAttachments(patientId, targetId);
      setAttachments(res.result?.attachments || res.attachments || []);
    } catch (err) {
      toast.error(err.message || "Failed to load attachments");
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!manageOpen) return undefined;
    loadAll();
    const onKey = (e) => {
      if (e.key === "Escape") setManageOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when dialog opens for this form
  }, [manageOpen, patientId, form.id, form.dailyLogId]);

  const openManage = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setEditingId(null);
    setManageOpen(true);
  };

  const closeManage = () => {
    setManageOpen(false);
    setEditingId(null);
  };

  const openFile = async (attachmentId) => {
    setOpeningId(attachmentId);
    try {
      const res = isDailyLog
        ? await downloadDailyLogAttachment(patientId, attachmentId, "view")
        : await downloadClinicalFormAttachment(patientId, attachmentId, "view");
      const url = res.result?.url || res.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.error("File URL unavailable");
    } catch (err) {
      toast.error(err.message || "Failed to open file");
    } finally {
      setOpeningId(null);
    }
  };

  const saveRename = async (attachmentId) => {
    const name = editName.trim();
    if (!name) {
      toast.error("Display name is required");
      return;
    }
    try {
      if (isDailyLog) {
        await updateDailyLogAttachment(patientId, attachmentId, {
          displayName: name,
        });
      } else {
        await updateClinicalFormAttachment(patientId, attachmentId, {
          displayName: name,
        });
      }
      toast.success("Attachment renamed");
      setEditingId(null);
      await loadAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Failed to rename");
    }
  };

  const removeAttachment = async (attachment) => {
    const ok = await confirm({
      title: "Delete attachment?",
      message: `"${attachment.displayName || attachment.originalName}" will be permanently removed from this form. This cannot be undone.`,
    });
    if (!ok) return;
    try {
      if (isDailyLog) {
        await deleteDailyLogAttachment(patientId, attachment.id);
      } else {
        await deleteClinicalFormAttachment(patientId, attachment.id);
      }
      toast.success("Attachment deleted");
      await loadAll();
      onChanged?.();
      if (
        attachments.length <= 1 ||
        (form.attachmentCount != null && form.attachmentCount <= 1)
      ) {
        closeManage();
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  if (!count) return null;

  const dialog =
    manageOpen &&
    createPortal(
      <div className={MODAL_BACKDROP} onClick={closeManage}>
        <div
          className={`${MODAL_PANEL} max-w-lg`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`att-title-${form.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={MODAL_HEADER}>
            <div className="min-w-0">
              <h3 id={`att-title-${form.id}`} className={MODAL_TITLE}>
                Attachments
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                {[
                  form.formName,
                  displayDateUS(clinicalFormScheduleDate(form)),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <button
              type="button"
              onClick={closeManage}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
              aria-label="Close"
            >
              <CloseIcon size={14} />
            </button>
          </div>
          <div className={MODAL_BODY}>
            {loading ? (
              <p className="text-sm text-slate-400 animate-pulse">Loading…</p>
            ) : attachments.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                No attachments
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex items-center gap-2 px-3 py-2 bg-white"
                  >
                    <PaperclipIcon
                      size={14}
                      className="text-slate-400 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      {editingId === att.id ? (
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full h-7 px-2 text-xs border border-slate-300 rounded-md outline-none focus:border-indigo-400"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(att.id);
                          }}
                        />
                      ) : (
                        <>
                          <p className="text-xs font-medium text-slate-800 truncate">
                            {att.displayName || att.originalName}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {att.uploadedByName || "—"} ·{" "}
                            {displayDateUS(att.uploadedAt) || "—"}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {editingId === att.id ? (
                        <>
                          <button
                            type="button"
                            className={`${BTN_PRIMARY} !h-7 !px-2 !text-[11px]`}
                            onClick={() => saveRename(att.id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className={`${BTN_SECONDARY} !h-7 !px-2 !text-[11px]`}
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="p-1 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded disabled:opacity-40"
                            onClick={() => openFile(att.id)}
                            disabled={openingId === att.id}
                            title="View"
                          >
                            <ViewIcon size={14} />
                          </button>
                          {canEdit ? (
                            <>
                              <button
                                type="button"
                                className="p-1 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded"
                                onClick={() => {
                                  setEditingId(att.id);
                                  setEditName(
                                    att.displayName || att.originalName || "",
                                  );
                                }}
                                title="Edit name"
                              >
                                <EditIcon size={14} />
                              </button>
                              <button
                                type="button"
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                onClick={() => removeAttachment(att)}
                                title="Delete"
                              >
                                <DeleteIcon size={14} />
                              </button>
                            </>
                          ) : null}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end pt-1">
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={closeManage}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        type="button"
        onClick={openManage}
        className="inline-flex items-center gap-0.5 h-6 px-1.5 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 shrink-0"
        title={`${count} attachment${count === 1 ? "" : "s"} — click to manage`}
        aria-label={`${count} attachments`}
      >
        <PaperclipIcon size={12} />
        <span className="tabular-nums">{count}</span>
      </button>
      {dialog}
      {/* Sibling of `dialog`: portal events bubble through the React tree, so
          nesting this inside would let a backdrop click close both layers. */}
      {confirmDialog}
    </>
  );
};

export default ClinicalFormAttachmentPin;
